<template>
    <div class="work-location-card-mobile">
        <div class="header">
            <div class="header-text">
                <h3 class="title">Work Location</h3>
                <p class="subtitle">Choose Your Work Location for Today</p>
            </div>
            <div class="button-wrapper">
                <AnimatedButton />
            </div>
        </div>

        <div class="tabs">
            <div class="tab" :class="{ active: workLocation === 'inOffice' }" @click="workLocation = 'inOffice'">
                <v-icon>mdi-briefcase</v-icon>
                <span>In-Office</span>
            </div>
            <div class="tab" :class="{ active: workLocation === 'remoteWork' }" @click="workLocation = 'remoteWork'">
                <v-icon>mdi-home</v-icon>
                <span>Remote Work</span>
            </div>
        </div>

        <div class="content">
            <div v-if="workLocation === 'inOffice'" class="in-office-grid">
                <div v-for="(card, index) in inOfficeCards" :key="index" class="info-card" :class="card.background">
                    <div class="info-text">
                        <p class="info-title">{{ card.title }}</p>
                        <p class="info-desc">{{ card.desc }}</p>
                    </div>
                    <img :src="card.image" class="info-image" />
                </div>
            </div>

            <div v-else class="remote-work">
                <!-- Not Configured State -->
                <div v-if="!isConfigured" class="error-state">
                    <v-icon size="48" color="warning">mdi-alert-circle-outline</v-icon>
                    <p class="error-title">Remote Work Not Configured</p>
                    <p class="error-desc">Please contact your administrator to set up Remote Work for this campus</p>
                </div>

                <!-- Configured State -->
                <div v-else>
                    <p class="section-header">Select Your Working Hours</p>

                    <div class="today-date">
                        <span class="label">Today</span>
                        <span class="date">{{ moment().format('DD/MM/YYYY') }}</span>
                    </div>

                    <v-row dense class="selects-grid">
                        <v-col cols="12" sm="6">
                            <v-select v-model="startTime" :items="times" label="Work Start"
                                :disabled="isSaveDisabled || isLoading" variant="outlined" density="comfortable"
                                rounded />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select v-model="endTime" :items="times" label="Work End"
                                :disabled="isSaveDisabled || isLoading" variant="outlined" density="comfortable"
                                rounded />
                        </v-col>
                        <v-col cols="12">
                            <v-select v-model="lunchDuration" :items="lunchDurations" :disabled="isSaveDisabled || isLoading"
                                label="Lunch Break" variant="outlined" density="comfortable"
                                rounded />
                        </v-col>
                        <v-col cols="12">
                            <v-combobox
                                v-model="note"
                                :items="noteSuggestions"
                                label="Optional Note"
                                :disabled="isSaveDisabled || isLoading"
                                variant="outlined"
                                density="comfortable"
                                rounded
                                maxlength="200"
                                counter
                                clearable
                                hint="Choose a suggestion or type your own"
                                persistent-hint
                            />
                        </v-col>
                    </v-row>

                    <v-alert v-if="errorMessage" type="error" class="mt-2" dense closable
                        @click:close="errorMessage = ''">
                        {{ errorMessage }}
                    </v-alert>

                    <v-alert v-if="successMessage" type="success" class="mt-2" dense closable
                        @click:close="successMessage = ''">
                        {{ successMessage }}
                    </v-alert>

                    <div class="save-button-container">
                        <v-btn v-if="!isSaveDisabled" color="primary" rounded class="save-button" :loading="isLoading"
                            @click="saveSchedule">
                            Save
                        </v-btn>

                        <v-btn v-else color="secondary" rounded variant="outlined" class="save-button"
                            @click="editSchedule">
                            Edit
                        </v-btn>

                        <p v-if="isSaveDisabled" class="save-hint">Schedule saved for today</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import moment from 'moment'
import AnimatedButton from '@/components/general/ChildComponents/AnimatedButton.vue'
import snacks from '@/assets/Branding/Pia/snacks.png'
import lunch from '@/assets/Branding/Pia/lunch.png'
import meeting from '@/assets/Branding/Pia/meeting.png'
import fun from '@/assets/Branding/Pia/fun.png'
import {
    getTodayRemoteWorkRecord,
    saveRemoteWorkRecord,
    validateTimes,
    isRemoteWorkConfigured
} from '@/services/RemoteWorkService'

const workLocation = ref('inOffice')
const startTime = ref('09:00')
const endTime = ref('18:00')
const lunchDuration = ref('30')
const note = ref('')
const isSaveDisabled = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const isConfigured = ref(true)
const currentDate = ref(moment().format('DD/MM/YYYY'))

const times = [
    '06:00', '06:30', '07:00', '07:30', '08:00', '08:30',
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00', '18:30', '19:00', '19:30', '20:00'
]

const lunchDurations = [
    { title: 'No Break', value: '0' },
    { title: '30 min', value: '30' },
    { title: '45 min', value: '45' },
    { title: '1 hour', value: '60' }
]

const noteSuggestions = [
    'Available all day',
    'Focused work - limited availability',
    'Meetings scheduled',
    'Available for calls',
    'Deep work session',
    'Flexible schedule',
    'Team collaboration time',
    'Client calls scheduled',
    'Training session',
    'Limited availability - urgent only'
]

const inOfficeCards = [
    {
        title: 'Enjoy Snacks',
        desc: 'Free snacks available',
        image: snacks,
        background: 'card-blue'
    },
    {
        title: 'Enjoy Lunch',
        desc: 'Free lunch provided',
        image: lunch,
        background: 'card-yellow'
    },
    {
        title: 'Enjoy Break',
        desc: 'Take regular breaks',
        image: meeting,
        background: 'card-lightblue'
    },
    {
        title: 'Enjoy Events',
        desc: 'Team events and activities',
        image: fun,
        background: 'card-purple'
    }
]

// Check configuration and load data
onMounted(async () => {
    try {
        isConfigured.value = await isRemoteWorkConfigured()
        if (isConfigured.value) {
            await loadTodayRecord()
        }
    } catch (error) {
        console.error('Error loading remote work data:', error)
        isConfigured.value = false
    }
})

// Load today's record
const loadTodayRecord = async () => {
    try {
        isLoading.value = true
        const record = await getTodayRemoteWorkRecord()

        if (record) {
            startTime.value = record.startTime
            endTime.value = record.endTime
            lunchDuration.value = record.breakDuration || '0'
            note.value = record.note || ''
            isSaveDisabled.value = true
        }
    } catch (error) {
        console.error('Error loading today record:', error)
    } finally {
        isLoading.value = false
    }
}

// Watch for date changes (reset at midnight)
watch(() => moment().format('DD/MM/YYYY'), async (newDate) => {
    if (newDate !== currentDate.value) {
        currentDate.value = newDate
        isSaveDisabled.value = false
        errorMessage.value = ''
        successMessage.value = ''
        if (isConfigured.value) {
            await loadTodayRecord()
        }
    }
})

// Save schedule
const saveSchedule = async () => {
    try {
        errorMessage.value = ''
        successMessage.value = ''

        // Validate times
        const validation = validateTimes(startTime.value, endTime.value, lunchDuration.value)
        if (!validation.valid) {
            errorMessage.value = validation.error
            return
        }

        isLoading.value = true

        await saveRemoteWorkRecord(
            startTime.value,
            endTime.value,
            lunchDuration.value,
            note.value
        )

        isSaveDisabled.value = true
        successMessage.value = 'Schedule saved successfully!'
    } catch (error) {
        errorMessage.value = 'Error saving schedule. Please try again.'
        console.error('Error saving schedule:', error)
    } finally {
        isLoading.value = false
    }
}

const editSchedule = () => {
    isSaveDisabled.value = false
    successMessage.value = ''
}
</script>

<style scoped>
.work-location-card-mobile {
    background: white;
    border-radius: 14px;
    padding: 20px;
    margin-top: 20px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}

.header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.header-text {
    text-align: center;
}

.button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
}

.title {
    font-weight: bold;
    font-size: 1.4rem;
}

.subtitle {
    font-size: 0.9rem;
    color: #6b7280;
}

.tabs {
    display: flex;
    margin-top: 20px;
    border-bottom: 1px solid #e0e0e0;
    justify-content: space-evenly;
    gap: 10px;
}

.tab {
    flex: 1;
    text-align: center;
    padding: 10px;
    cursor: pointer;
    color: #374151;
    font-weight: 500;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
}

.tab.active {
    color: #2563eb;
    font-weight: bold;
    border-bottom: 2px solid #2563eb;
}

.content {
    margin-top: 20px;
}

/* In-Office */
.in-office-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
}

.info-card {
    background: #f3f4f6;
    border-radius: 12px;
    padding: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 100px;
}

.info-text {
    flex: 1;
    margin-right: 8px;
}

.info-title {
    font-weight: bold;
    font-size: 14px;
    margin-bottom: 4px;
}

.info-desc {
    font-size: 12px;
    color: #6b7280;
}

.info-image {
    width: 80px;
    height: 60px;
    object-fit: cover;
    clip-path: polygon(40% 0%, 100% 0%, 100% 100%, 0 100%);
}

/* Remote Work */
.remote-work {
    margin-top: 10px;
}

.error-state {
    text-align: center;
    padding: 40px 20px;
}

.error-title {
    font-weight: bold;
    font-size: 18px;
    margin-top: 16px;
    color: #374151;
}

.error-desc {
    font-size: 14px;
    color: #6b7280;
    margin-top: 8px;
}

.section-header {
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 10px;
}

.today-date {
    display: flex;
    justify-content: space-between;
    background: #f3f4f6;
    padding: 8px 14px;
    border-radius: 8px;
    font-weight: bold;
    margin-bottom: 10px;
}

.label {
    color: #374151;
}

.date {
    color: #2563eb;
}

.selects-grid {
    margin-top: 10px;
}

.save-button-container {
    align-self: flex-end;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-top: 10px;
}

.save-button {
    min-width: 100px;
}

.save-hint {
    font-size: 12px;
    color: #10b981;
    margin-top: 5px;
    text-align: right;
    font-weight: 500;
}

/* Colors */
.card-blue {
    background: #eff6ff;
}

.card-yellow {
    background: #fef9c3;
}

.card-lightblue {
    background: #e0f2fe;
}

.card-purple {
    background: #f3e8ff;
}

/* 📱 Responsive Fixes */
@media (max-width: 770px) {
    .info-title {
        font-size: 13px;
    }

    .info-desc {
        font-size: 11px;
    }

    .info-image {
        width: 70px;
        height: 50px;
    }
}

@media (max-width: 600px) {
    .tabs {
        gap: 8px;
    }

    .tab {
        padding: 8px;
        font-size: 14px;
    }
}

@media (max-width: 480px) {
    .in-office-grid {
        grid-template-columns: 1fr;
    }

    .info-card {
        padding: 10px;
    }

    .info-title {
        font-size: 13px;
    }

    .info-desc {
        font-size: 10px;
    }

    .info-image {
        width: 60px;
        height: 40px;
    }
}

@media (max-width: 375px) {
    .info-title {
        font-size: 12px;
    }

    .info-desc {
        font-size: 9.5px;
    }

    .info-card {
        padding: 8px;
    }
}
</style>